/**
 * Custom ConfigDrivenSuperTableComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfigDrivenSuperTableComponent as ParentConfigDrivenSuperTableComponent, SuperDynamicChartHostComponent as SharedSuperDynamicChartHostComponent } from '@shared-lib/chart-components/config-driven-super-table/config-driven-super-table.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { CustomConfigService } from '../../shared-services/custom-config.service';

const WRAP_DEBUG_KEY = 'PAT_WRAP_DEBUG_OEM_POSITIONING';

@Component({
  selector: 'app-config-driven-super-table',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, SharedSuperDynamicChartHostComponent],
  templateUrl: './config-driven-super-table.component.html',
  styleUrls: ['./config-driven-super-table.component.scss'],
  providers: [{ provide: ConfigService, useExisting: CustomConfigService }],
})
export class ConfigDrivenSuperTableComponent extends ParentConfigDrivenSuperTableComponent {
  private apiWeightageConfig: any = null;
  private editedRowsByIdentity: Record<string, Record<string, any>> = {};
  private lastEditedIdentity = '';
  private wrapDebugSequence = 0;
  private selectOptionsCache: Record<string, { signature: string; options: any[] }> = {};

  get hasWeightageRow(): boolean {
    if ((this.data as any)?.hideWeightageRow === true) {
      return false;
    }
    return false;
  }

  get headerRowOffset(): number {
    return this.hasWeightageRow ? 1 : 0;
  }

  get footerActions(): Array<any> {
    const actions = (this.data as any)?.footerActions;
    return Array.isArray(actions) ? actions : [];
  }

  private get tableDecorations(): any {
    const cfg = (this.data as any)?.tableDecorations;
    return cfg && typeof cfg === 'object' ? cfg : {};
  }

  get showCriteriaBand(): boolean {
    return this.tableDecorations?.criteriaBand?.enabled === true;
  }

  get criteriaBandLabel(): string {
    const raw = this.tableDecorations?.criteriaBand?.label ?? 'Criteria';
    const text = String(raw).trim();
    if (text === '') return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  private toLowerCamelCase(input: any): string {
    if (input === undefined || input === null) return '';
    const text = String(input).trim();
    if (text === '') return '';
    const parts = text.split(/[\s_\-]+/).filter(p => p.length > 0);
    if (parts.length === 0) return '';
    const first = parts[0].toLowerCase();
    const rest = parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
    return [first, ...rest].join('');
  }

  get criteriaBandWidthPx(): number {
    const value = Number(this.tableDecorations?.criteriaBand?.width || 180);
    return Number.isFinite(value) && value > 0 ? value : 180;
  }

  get criteriaBandHeightPx(): number {
    const rows = Array.isArray(this.flatRows) ? this.flatRows.length : 0;
    const footerRows = this.showBottomScoreRow ? 1 : 0;
    return (rows + footerRows) * Number(this.rowHeight || 52);
  }

  get showBottomScoreRow(): boolean {
    return this.tableDecorations?.bottomScoreRow?.enabled === true;
  }

  get bottomScoreLabel(): string {
    return String(this.tableDecorations?.bottomScoreRow?.label || 'Score');
  }

  get bottomScoreLabelColspan(): number {
    const configured = Number(this.tableDecorations?.bottomScoreRow?.labelColspan || 1);
    const visibleCount = Array.isArray(this.visibleColumns) ? this.visibleColumns.length : 0;
    if (!Number.isFinite(configured) || configured < 1) return 1;
    return Math.min(Math.floor(configured), Math.max(1, visibleCount - 1));
  }

  get bottomScoreValues(): string[] {
    const configured = this.tableDecorations?.bottomScoreRow?.values;
    const values = Array.isArray(configured) ? configured.map((item: any) => String(item)) : [];

    const visibleCount = Array.isArray(this.visibleColumns) ? this.visibleColumns.length : 0;
    const slots = Math.max(0, visibleCount - this.bottomScoreLabelColspan);
    if (slots === 0) return [];

    const output = values.slice(0, slots);
    while (output.length < slots) output.push('');
    return output;
  }

  get weightageCells(): Array<any> {
    const groups = this.getConfiguredColumnGroups();
    const visibleColumns = Array.isArray(this.visibleColumns) ? this.visibleColumns : [];
    if (!Array.isArray(groups) || groups.length === 0 || visibleColumns.length === 0) {
      return [];
    }

    const weightageSource = this.getWeightageSource();
    const byKey = weightageSource?.byKey && typeof weightageSource.byKey === 'object'
      ? weightageSource.byKey
      : {};

    const cells: Array<any> = [];
    groups.forEach((group: any, groupIndex: number) => {
      const groupColumns = Array.isArray(group?.columns)
        ? group.columns.map((key: any) => String(key)).filter((key: string) => key)
        : [];

      const leaves = visibleColumns.filter((column: any) => groupColumns.includes(column.key));
      if (leaves.length === 0) return;

      const key = String(group?.key || '').trim();
      const label = String(group?.label || '').trim();
      const candidateValues = [
        key ? byKey[key] : undefined,
        label ? byKey[label] : undefined,
      ];
      const value = candidateValues.find((item: any) => item !== undefined && item !== null);
      const displayValue = value === undefined || value === null ? '' : String(value);
      const numericValue = Number(displayValue);
      const editable = key !== 'oem' && displayValue.trim() !== '' && Number.isFinite(numericValue);

      cells.push({
        id: `weightage-${groupIndex}-${key || groupColumns[0] || 'group'}`,
        key,
        value: displayValue,
        editable,
        colspan: leaves.length,
        widthPx: leaves.reduce((sum: number, column: any) => sum + Number(column.widthPx || 0), 0),
        minWidthPx: leaves.reduce((sum: number, column: any) => {
          const minWidth = Number(String(column.minWidthCss || '').replace('px', ''));
          return sum + (Number.isFinite(minWidth) ? minWidth : Number(column.widthPx || 0));
        }, 0),
        leftOffset: Number(leaves[0]?.leftOffset || 0),
        frozen: leaves.every((column: any) => this.visibleColumns.indexOf(column) < this.freezeLeftColumns || column.sticky === 'left'),
      });
    });

    return cells;
  }

  trackByWeightageCell = (_: number, cell: any): string => cell.id;

  isWeightageEditable(cell: any): boolean {
    return !!cell?.editable;
  }

  onWeightageValueChange(cell: any, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const raw = String(inputElement?.value ?? '').trim();
    const nextValue = raw === '' ? '' : String(Number(raw));
    if (raw !== '' && !Number.isFinite(Number(raw))) return;

    if (cell) {
      cell.value = nextValue;
    }

    const source = this.getOrCreateWeightageSource();
    if (cell?.key) {
      source.byKey[cell.key] = nextValue;
    }

    const tableConfig = this.data as any;
    if (tableConfig?.weightage?.byKey && cell?.key) {
      tableConfig.weightage.byKey[cell.key] = nextValue;
    }

    this.cdr.markForCheck();
  }

  getWeightageCellStyle(cell: any): any {
    const style: any = {
      width: `${cell.widthPx}px`,
      'min-width': `${cell.minWidthPx || cell.widthPx}px`,
      height: `${this.headerRowHeight}px`,
      top: '0px',
      position: 'sticky',
      'z-index': cell.frozen ? 700 : 660,
      'text-align': 'center',
    };

    if (cell.frozen) {
      style.left = `${cell.leftOffset}px`;
    }

    return style;
  }

  get weightageCellsPerColumn(): Array<any> {
    const visibleColumns = Array.isArray(this.visibleColumns) ? this.visibleColumns : [];
    const cells = this.weightageCells || [];
    const byKey: Record<string, any> = {};
    cells.forEach((c: any) => {
      byKey[String(c.key || c.id || '')] = c;
      byKey[String(Number(c.leftOffset || 0))] = c;
    });
    return visibleColumns.map((col: any) => byKey[String(col.key)] || byKey[String(Number(col.leftOffset || 0))] || { id: `weightage-placeholder-${col.key}`, key: col.key, value: '' });
  }

  private formatMillions(num: number): string {
    const millions = num / 1e6;
    if (!Number.isFinite(millions)) return '';
    if (Math.abs(millions) >= 10) return `$${Math.round(millions)}M`;
    if (Math.abs(millions) >= 1) {
      if (millions % 1 === 0) {
        return `$${Math.round(millions)}M`;
      } else {
        return `$${millions.toFixed(1)}M`;
      }
    }
    return `$${millions.toFixed(2)}M`;
  }

  private parseMillionsInput(input: string): number | null {
    if (input === undefined || input === null) return null;
    let raw = String(input).trim();
    if (raw === '') return null;
    raw = raw.replace(/,/g, '').replace(/\$/g, '');
    const last = raw.slice(-1).toLowerCase();
    if (last === 'm') {
      const v = parseFloat(raw.slice(0, -1));
      return Number.isFinite(v) ? v * 1e6 : null;
    }
    if (last === 'k') {
      const v = parseFloat(raw.slice(0, -1));
      return Number.isFinite(v) ? v * 1e3 : null;
    }
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }

  private formatWithPercentage(rawValue: any): any {
    if (rawValue === undefined || rawValue === null) return rawValue;
    const rawText = String(rawValue).trim();
    if (rawText === '') return rawValue;
    if (rawText.endsWith('%')) return rawText;
    const asNum = Number(rawText.replace(/,/g, ''));
    if (!Number.isFinite(asNum)) return rawValue;
    return `${rawText}%`;
  }

  private shouldShowPercentage(row: any, column: any, resolved: any): boolean {
    if (resolved?.showPercentage === true || column?.showPercentage === true) {
      return true;
    }

    const key = String(column?.key || resolved?.key || '').trim();
    if (!key) return false;

    const sourceColumns = Array.isArray((this.data as any)?.columns) ? (this.data as any).columns : [];
    const sourceConfig = sourceColumns.find((c: any) => String(c?.key || '') === key);
    return sourceConfig?.showPercentage === true;
  }

  private getWeightageSource(): any {
    const tableConfig = this.data as any;
    return this.apiWeightageConfig || tableConfig?.weightage || tableConfig?.weightages || null;
  }

  private getOrCreateWeightageSource(): any {
    if (!this.apiWeightageConfig || typeof this.apiWeightageConfig !== 'object') {
      this.apiWeightageConfig = null;
    }

    if (this.apiWeightageConfig) {
      if (!this.apiWeightageConfig.byKey || typeof this.apiWeightageConfig.byKey !== 'object') {
        this.apiWeightageConfig.byKey = {};
      }
      return this.apiWeightageConfig;
    }

    const tableConfig = this.data as any;
    if (!tableConfig.weightage || typeof tableConfig.weightage !== 'object') {
      tableConfig.weightage = { byKey: {} };
    }
    if (!tableConfig.weightage.byKey || typeof tableConfig.weightage.byKey !== 'object') {
      tableConfig.weightage.byKey = {};
    }
    return tableConfig.weightage;
  }

  private extractWeightageFromResponse(response: any): any {
    if (!response || typeof response !== 'object') return null;

    const direct = (response as any).weightage || (response as any).weightages;
    if (direct && typeof direct === 'object') return direct;

    const responseList = (response as any).responseList;
    if (Array.isArray(responseList) && responseList.length > 0) {
      const firstEntry = responseList[0];
      const nestedDirect = firstEntry?.weightage || firstEntry?.weightages;
      if (nestedDirect && typeof nestedDirect === 'object') return nestedDirect;

      const nestedData = Array.isArray(firstEntry?.data) ? firstEntry.data[0] : null;
      const nestedPayloadWeightage = nestedData?.weightage || nestedData?.weightages;
      if (nestedPayloadWeightage && typeof nestedPayloadWeightage === 'object') return nestedPayloadWeightage;
    }

    return null;
  }

  // Constructor is inherited from parent to avoid dependency injection issues

  override initializeComponent(): any {
    // TODO: Add your custom logic here
    return super.initializeComponent();
  }

  override subscribeToFilters(): any {
    // TODO: Add your custom logic here
    return super.subscribeToFilters();
  }

  override shouldReloadForFilters(filters: any): any {
    // TODO: Add your custom logic here
    return super.shouldReloadForFilters(filters);
  }

  override loadRows(filters?: any): any {
    // TODO: Add your custom logic here
    return super.loadRows(filters);
  }

  override rebuildTableModel(): any {
    const result = super.rebuildTableModel();
    this.logWrapDebug('model');
    setTimeout(() => this.logWrapDebug('dom'), 0);
    return result;
  }

  override initializeControlValues(): any {
    // TODO: Add your custom logic here
    return super.initializeControlValues();
  }

  override resolveControlOptions(control: any): any {
    // TODO: Add your custom logic here
    return super.resolveControlOptions(control);
  }

  override loadControlOptions(filters: any, initialLoad: any): any {
    // TODO: Add your custom logic here
    return super.loadControlOptions(filters, initialLoad);
  }

  override loadControlOptionsForControl(control: any, filters: any): any {
    // TODO: Add your custom logic here
    return super.loadControlOptionsForControl(control, filters);
  }

  override buildControlOptionsRequestConfig(control: any): any {
    // TODO: Add your custom logic here
    return super.buildControlOptionsRequestConfig(control);
  }

  override extractControlOptionsFromResponse(response: any, control: any): any {
    // TODO: Add your custom logic here
    return super.extractControlOptionsFromResponse(response, control);
  }

  override syncControlValueWithOptions(control: any): any {
    // TODO: Add your custom logic here
    return super.syncControlValueWithOptions(control);
  }

  override extractInlineRows(): any {
    // TODO: Add your custom logic here
    return super.extractInlineRows();
  }

  override extractRows(response: any): any {
    this.apiWeightageConfig = this.extractWeightageFromResponse(response);
    const rows = super.extractRows(response);
    if ((this.data as any)?.bomPivotEnabled) {
      const pivoted = this.pivotBomData(rows);
      // Reset cached columns so rebuildTableModel re-normalizes from the updated this.data.columns
      this.columns = [];
      return this.normalizeScoringConfigurationRows(pivoted);
    }
    return this.normalizeScoringConfigurationRows(rows);
  }

  private readonly BOM_MONTH_ABBR = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  private readonly BOM_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  private pivotBomData(bomList: any[]): any[] {
    if (!Array.isArray(bomList) || bomList.length === 0) return bomList;

    // Collect unique pivot combinations (year, month, bom_esn_wo) to build dynamic columns
    const pivotComboMap = new Map<string, { year: number; month: number; woRaw: string; monthAbbr: string; woSuffix: string }>();

    for (const item of bomList) {
      const m = Number(item.month);
      const y = Number(item.year);
      const woRaw = String(item.bom_esn_wo ?? '');
      const woSuffix = woRaw.includes('|') ? woRaw.split('|')[1] : woRaw;
      const monthAbbr = m >= 1 && m <= 12 ? this.BOM_MONTH_ABBR[m - 1] : null;
      if (!monthAbbr || !woSuffix || !y) continue;

      const comboKey = `${y}_${m}_${woRaw}`;
      if (!pivotComboMap.has(comboKey)) {
        pivotComboMap.set(comboKey, { year: y, month: m, woRaw, monthAbbr, woSuffix });
      }
    }

    // Sort combos by year → month → wo
    const sortedCombos = Array.from(pivotComboMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      if (a.month !== b.month) return a.month - b.month;
      return a.woRaw.localeCompare(b.woRaw);
    });

    // Dynamically update this.data.columns and this.data.columnGroups
    this.applyBomDynamicColumns(sortedCombos);

    // Group by ecc_code, then by mrc_name
    const eccMap = new Map<string, { designConfig: string; childMap: Map<string, any[]> }>();

    for (const item of bomList) {
      const eccKey = String(item.ecc_code ?? '');
      const mrcKey = String(item.mrc_name ?? '');

      if (!eccMap.has(eccKey)) {
        eccMap.set(eccKey, { designConfig: String(item.design_config ?? ''), childMap: new Map() });
      }
      const eccEntry = eccMap.get(eccKey)!;
      if (item.design_config) eccEntry.designConfig = String(item.design_config);

      if (!eccEntry.childMap.has(mrcKey)) eccEntry.childMap.set(mrcKey, []);
      eccEntry.childMap.get(mrcKey)!.push(item);
    }

    const rows: any[] = [];
    let idx = 0;

    eccMap.forEach((eccEntry, eccCode) => {
      const eccAgg: Record<string, number> = {};
      const children: any[] = [];

      eccEntry.childMap.forEach((items, mrcName) => {
        const childRow: any = {
          id: `bom-child-${idx++}`,
          design_config: eccEntry.designConfig,
          ecc_code: eccCode,
          current_part_number: mrcName,
        };

        for (const item of items) {
          const m = Number(item.month);
          const monthAbbr = m >= 1 && m <= 12 ? this.BOM_MONTH_ABBR[m - 1] : null;
          const woRaw = String(item.bom_esn_wo ?? '');
          const woSuffix = woRaw.includes('|') ? woRaw.split('|')[1] : woRaw;

          if (!monthAbbr || !woSuffix) continue;

          const qtyKey = `${monthAbbr}_${woSuffix}_qty`;
          const priceKey = `${monthAbbr}_${woSuffix}_price`;

          childRow[qtyKey] = (childRow[qtyKey] ?? 0) + Number(item.sum_quantity ?? 0);
          childRow[priceKey] = (childRow[priceKey] ?? 0) + Number(item.extended_price ?? 0);
          eccAgg[qtyKey] = (eccAgg[qtyKey] ?? 0) + Number(item.sum_quantity ?? 0);
          eccAgg[priceKey] = (eccAgg[priceKey] ?? 0) + Number(item.extended_price ?? 0);
        }

        children.push(childRow);
      });

      rows.push({
        id: `bom-ecc-${idx++}`,
        design_config: eccEntry.designConfig,
        ecc_code: eccCode,
        current_part_number: '',
        children,
        ...eccAgg,
      });
    });

    return rows;
  }

  private applyBomDynamicColumns(combos: Array<{ year: number; month: number; woRaw: string; monthAbbr: string; woSuffix: string }>): void {
    const tableConfig = this.data as any;
    if (!tableConfig) return;

    // Preserve the sticky left columns (design_config, ecc_code, current_part_number)
    const originalColumns: any[] = Array.isArray(tableConfig.columns) ? tableConfig.columns : [];
    const staticColumns = originalColumns.filter((c: any) => c.sticky === 'left');

    // Build a dynamic column per pivot combo (qty + price pair)
    const dynamicColumns: any[] = [];
    for (const combo of combos) {
      dynamicColumns.push(
        { key: `${combo.monthAbbr}_${combo.woSuffix}_qty`, label: 'Sum of [SumQuantity]', width: 155, type: 'number', align: 'right' },
        { key: `${combo.monthAbbr}_${combo.woSuffix}_price`, label: 'Extended Price by PN', width: 165, type: 'number', align: 'right' },
      );
    }
    tableConfig.columns = [...staticColumns, ...dynamicColumns];

    // Build columnGroups: Date[Calendar Month] → {Month Year} → {bom_esn_wo}
    const monthGroupMap = new Map<string, { year: number; month: number; woCombos: typeof combos }>();
    for (const combo of combos) {
      const key = `${combo.year}_${combo.month}`;
      if (!monthGroupMap.has(key)) {
        monthGroupMap.set(key, { year: combo.year, month: combo.month, woCombos: [] });
      }
      monthGroupMap.get(key)!.woCombos.push(combo);
    }

    const monthChildren = Array.from(monthGroupMap.values()).map(mg => ({
      label: `${this.BOM_MONTH_NAMES[mg.month - 1]} ${mg.year}`,
      children: mg.woCombos.map(combo => ({
        label: combo.woRaw,
        columns: [`${combo.monthAbbr}_${combo.woSuffix}_qty`, `${combo.monthAbbr}_${combo.woSuffix}_price`],
      })),
    }));

    tableConfig.columnGroups = [{ label: 'Date[Calendar Month]', children: monthChildren }];
  }

  private getScoringMappingConfig(): any {
    const cfg = (this.data as any)?.scoringMapping;
    return cfg && typeof cfg === 'object' ? cfg : null;
  }

  private normalizeScoringConfigurationRows(rows: any): any {
    if (!Array.isArray(rows) || rows.length === 0) return rows;

    const mapping = this.getScoringMappingConfig();
    if (!mapping?.enabled) return rows;

    const factorKey = String(mapping.factorKey || 'Factor');
    const metricNameKey = String(mapping.metricNameKey || 'Metric_Name');
    const weightageKey = String(mapping.weightageKey || 'Weightage');
    const valuesKey = String(mapping.valuesKey || 'Values');
    const scoreKey = String(mapping.scoreKey || 'score');
    const labelKey = String(mapping.labelKey || 'category_label');

    const outputKeys = mapping.outputKeys && typeof mapping.outputKeys === 'object' ? mapping.outputKeys : {};
    const factorOutputKey = String(outputKeys.factor || 'factor');
    const weightageOutputKey = String(outputKeys.weightage || 'weightage');

    const scoreColumnMap = mapping.scoreColumnMap && typeof mapping.scoreColumnMap === 'object'
      ? mapping.scoreColumnMap
      : {
          '9': String(outputKeys.value9 || 'value_9'),
          '3': String(outputKeys.value3 || 'value_3'),
          '1': String(outputKeys.value1 || 'value_1'),
          '0': String(outputKeys.value0 || 'value_0'),
        };

    const hasValuesArray = rows.some((row: any) => Array.isArray(this.getValueByPath(row, valuesKey)));
    if (!hasValuesArray) return rows;

    const mappedRows = rows.map((row: any) => {
      const values = this.getValueByPath(row, valuesKey);
      const valueItems = Array.isArray(values) ? values : [];
      const scoreToLabel: Record<string, any> = {};

      valueItems.forEach((item: any) => {
        const rawScore = this.getValueByPath(item, scoreKey);
        if (rawScore === undefined || rawScore === null || rawScore === '') return;

        const numericScore = Number(rawScore);
        const normalizedScore = Number.isFinite(numericScore) ? String(numericScore) : String(rawScore);
        scoreToLabel[normalizedScore] = this.getValueByPath(item, labelKey) ?? '';
      });

      const mapped: any = {
        ...row,
        [factorOutputKey]: this.getValueByPath(row, factorKey) ?? this.getValueByPath(row, metricNameKey) ?? '',
        [weightageOutputKey]: this.getValueByPath(row, weightageKey) ?? '',
      };

      Object.keys(scoreColumnMap).forEach((score: string) => {
        const outKey = String(scoreColumnMap[score] || '').trim();
        if (!outKey) return;
        mapped[outKey] = scoreToLabel[score] ?? '';
      });

      return mapped;
    });

    const factorOrder = Array.isArray(mapping.factorOrder)
      ? mapping.factorOrder.map((item: any) => String(item).toLowerCase())
      : [];
    if (factorOrder.length === 0) return mappedRows;

    const rankByFactor: Record<string, number> = {};
    factorOrder.forEach((name: string, index: number) => {
      rankByFactor[name] = index;
    });

    return mappedRows.sort((left: any, right: any) => {
      const leftFactor = String(left?.[factorOutputKey] ?? '').toLowerCase();
      const rightFactor = String(right?.[factorOutputKey] ?? '').toLowerCase();
      const leftRank = rankByFactor[leftFactor];
      const rightRank = rankByFactor[rightFactor];

      const hasLeftRank = Number.isFinite(leftRank);
      const hasRightRank = Number.isFinite(rightRank);
      if (hasLeftRank && hasRightRank) return leftRank - rightRank;
      if (hasLeftRank) return -1;
      if (hasRightRank) return 1;
      return 0;
    });
  }

  override applyRowGrouping(rows: any): any {
    // TODO: Add your custom logic here
    return super.applyRowGrouping(rows);
  }

  override getRowGroupingLevels(): any {
    // TODO: Add your custom logic here
    return super.getRowGroupingLevels();
  }

  override groupRowsByLevels(rows: any, levels: any, depth: any, parentPath: any): any {
    // TODO: Add your custom logic here
    return super.groupRowsByLevels(rows, levels, depth, parentPath);
  }

  override resolveRowGroupLabelColumnKey(): any {
    // TODO: Add your custom logic here
    return super.resolveRowGroupLabelColumnKey();
  }

  override buildRowGroupLabel(level: any, value: any): any {
    // TODO: Add your custom logic here
    return super.buildRowGroupLabel(level, value);
  }

  override buildGroupRowCellValues(sourceRows: any, labelColumnKey: any, currentLevelKey: any): any {
    // TODO: Add your custom logic here
    return super.buildGroupRowCellValues(sourceRows, labelColumnKey, currentLevelKey);
  }

  override resolveGroupCellValue(column: any, sourceRows: any): any {
    // TODO: Add your custom logic here
    return super.resolveGroupCellValue(column, sourceRows);
  }

  override normalizeColumns(sourceRows: any): any {
    // TODO: Add your custom logic here
    return super.normalizeColumns(sourceRows);
  }

  override normalizeColumn(column: any, index: any): any {
    const normalized: any = super.normalizeColumn(column, index);
    const source: any = column || {};
    return {
      ...normalized,
      wrapText: source.wrapText ?? source.textWrap ?? source.wrap ?? normalized.wrapText ?? false,
      textWrap: source.textWrap ?? normalized.textWrap,
      wrap: source.wrap ?? normalized.wrap,
    };
  }

  override buildVisibleColumns(columns: any): any {
    const parentColumns = super.buildVisibleColumns(columns);
    return parentColumns.map((col: any) => {
      const original = columns.find((c: any) => c.key === col.key);
      if (!original) return col;
      
      const widthVal = original.width || original.minWidth;
      const minWidthVal = original.minWidth || original.width;
      
      const isCustomCss = (val: any) => typeof val === 'string' && (val.includes('%') || val.includes('vw') || val === 'auto');
      
      if (isCustomCss(widthVal)) {
        col.widthCss = widthVal;
      }
      if (isCustomCss(minWidthVal)) {
        col.minWidthCss = minWidthVal;
      }
      
      // Also allow overriding pixel min-widths if responsiveness is desired
      if (original.responsive) {
         col.minWidthCss = '0px';
      }
      return col;
    });
  }

  override computeMinTableWidth(): any {
    // TODO: Add your custom logic here
    return super.computeMinTableWidth();
  }

  override getConfiguredColumnGroups(): any {
    // TODO: Add your custom logic here
    return super.getConfiguredColumnGroups();
  }

  override buildHeaderRows(visibleColumns: any): any {
    const rows = super.buildHeaderRows(visibleColumns);
    const hideLeafHeaderRow = (this.data as any)?.hideColumnTitleRow === true;
    if (!hideLeafHeaderRow || !Array.isArray(rows) || rows.length <= 1) {
      return rows;
    }
    return [rows[0]];
  }

  override buildHeaderNodeFromGroup(group: any, nodeId: any, columnsByKey: any, consumed: any): any {
    // TODO: Add your custom logic here
    return super.buildHeaderNodeFromGroup(group, nodeId, columnsByKey, consumed);
  }

  override createHeaderLeafNode(column: any, nodeId: any): any {
    // TODO: Add your custom logic here
    return super.createHeaderLeafNode(column, nodeId);
  }

  override getHeaderDepth(node: any): any {
    // TODO: Add your custom logic here
    return super.getHeaderDepth(node);
  }

  override collectHeaderLeafColumns(node: any): any {
    // TODO: Add your custom logic here
    return super.collectHeaderLeafColumns(node);
  }

  override appendHeaderCells(node: any, depth: any, maxDepth: any, rows: any, frozenByKey: any, path: any): any {
    // TODO: Add your custom logic here
    return super.appendHeaderCells(node, depth, maxDepth, rows, frozenByKey, path);
  }

  override getHeaderStackHeight(): any {
    const baseHeight = super.getHeaderStackHeight();
    if (!this.hasWeightageRow) return baseHeight;
    return baseHeight + this.headerRowHeight;
  }

  override getHeaderCellSortIndicator(cell: any): any {
    // TODO: Add your custom logic here
    return super.getHeaderCellSortIndicator(cell);
  }

  override onHeaderCellClick(cell: any, event?: any): any {
    // TODO: Add your custom logic here
    return super.onHeaderCellClick(cell, event);
  }

  override isSortEnabled(): any {
    // TODO: Add your custom logic here
    return super.isSortEnabled();
  }

  override isColumnSortable(column: any): any {
    // TODO: Add your custom logic here
    return super.isColumnSortable(column);
  }

  override onHeaderClick(column: any): any {
    // TODO: Add your custom logic here
    return super.onHeaderClick(column);
  }

  override getSortIndicator(column: any): any {
    // TODO: Add your custom logic here
    return super.getSortIndicator(column);
  }

  override sortRows(rows: any): any {
    // TODO: Add your custom logic here
    return super.sortRows(rows);
  }

  override sortChildren(row: any): any {
    // TODO: Add your custom logic here
    return super.sortChildren(row);
  }

  override compareByColumn(a: any, b: any, col?: any): any {
    // TODO: Add your custom logic here
    return super.compareByColumn(a, b, col);
  }

  override flattenRows(rows: any): any {
    // TODO: Add your custom logic here
    return super.flattenRows(rows);
  }

  override buildRowId(row: any, depth: any, index: any, parentId: any): any {
    // TODO: Add your custom logic here
    return super.buildRowId(row, depth, index, parentId);
  }

  override isRowExpanded(row: any, rowId: any, depth: any): any {
    if ((this.data as any)?.bomPivotEnabled) {
      if (this.expandedState[rowId] !== undefined) {
        return this.expandedState[rowId];
      }
      const level = Number(depth);
      return level < 1;
    }
    return super.isRowExpanded(row, rowId, depth);
  }

  override isDrillDownEnabled(): any {
    // TODO: Add your custom logic here
    return super.isDrillDownEnabled();
  }

  override showToggleInColumn(column: any, columnIndex: any): any {
    // TODO: Add your custom logic here
    return super.showToggleInColumn(column, columnIndex);
  }

  override toggleRow(row: any, event?: any): any {
    // TODO: Add your custom logic here
    return super.toggleRow(row, event);
  }

  override getRowSerial(rowIndex: any): any {
    // TODO: Add your custom logic here
    return super.getRowSerial(rowIndex);
  }

  override getSelectionKeyForSerialColumn(column: any): any {
    // TODO: Add your custom logic here
    return super.getSelectionKeyForSerialColumn(column);
  }

  override isRowSelected(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.isRowSelected(row, column);
  }

  override onSerialSelectionChange(row: any, column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onSerialSelectionChange(row, column, event);
  }

  override isAllRowsSelected(column: any): any {
    // TODO: Add your custom logic here
    return super.isAllRowsSelected(column);
  }

  override isSomeRowsSelected(column: any): any {
    // TODO: Add your custom logic here
    return super.isSomeRowsSelected(column);
  }

  override onSelectAllRowsChange(column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onSelectAllRowsChange(column, event);
  }

  override setRowSelected(row: any, column: any, selected: any): any {
    // TODO: Add your custom logic here
    return super.setRowSelected(row, column, selected);
  }

  override applySelectionToDescendants(nodes: any, depth: any, parentId: any, selected: any): any {
    // TODO: Add your custom logic here
    return super.applySelectionToDescendants(nodes, depth, parentId, selected);
  }

  override getSelectedRowIds(): any {
    // TODO: Add your custom logic here
    return super.getSelectedRowIds();
  }

  override isFrozenColumn(index: any, column: any): any {
    // TODO: Add your custom logic here
    return super.isFrozenColumn(index, column);
  }

  override isLastFrozenColumn(index: any, column: any): any {
    // TODO: Add your custom logic here
    return super.isLastFrozenColumn(index, column);
  }

  override isLastFrozenRenderColumn(column?: any): any {
    // TODO: Add your custom logic here
    return super.isLastFrozenRenderColumn(column);
  }

  override isFrozenRow(rowIndex: any): any {
    // TODO: Add your custom logic here
    return super.isFrozenRow(rowIndex);
  }

  override getHeaderCellStyle(cell: any, rowIndex: any): any {
    const style = super.getHeaderCellStyle(cell, rowIndex + this.headerRowOffset);
    style['text-align'] = 'center';

    const parseFontSize = (value: any): number => {
      const direct = Number(value);
      if (Number.isFinite(direct)) return direct;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return NaN;
    };

    const perColumnHeaderFontSize = parseFontSize(cell?.column?.headerFontSize);
    const widgetHeaderFontSize = parseFontSize((this.data as any)?.headerFontSize);
    const headerFontSize = Number.isFinite(perColumnHeaderFontSize)
      ? perColumnHeaderFontSize
      : widgetHeaderFontSize;

    if (Number.isFinite(headerFontSize) && headerFontSize > 0) {
      style['font-size'] = `${headerFontSize}px`;
    }

    return style;
  }

  override getBodyCellStyle(row: any, rowIndex: any, column: any, colIndex: any): any {
    const style = super.getBodyCellStyle(row, rowIndex, column, colIndex);
    try {
      const rowData = row?.source || row;
      const resolved = this.getResolvedCellConfig(rowData, column);

      if (this.shouldWrap(rowData, column)) {
        style['height'] = 'auto';
        style['min-height'] = `${this.rowHeight}px`;
        style['vertical-align'] = 'top';
      }

      if (resolved?.type === 'heatmap') {
        const rawValue = this.getCellValue(rowData, column);
        const numericValue = Number(rawValue);
        if (Number.isFinite(numericValue)) {
          style['background-color'] = this.resolveHeatmapColor(numericValue, resolved?.heatmapConfig);
          style['font-weight'] = '700';
          style['color'] = '#0b0f14';
        }
      }

      const columnKey = String(column?.key || '');
      if (columnKey === 'avgIMRevenue' || columnKey === 'avgCMRevenue') {
        const wlColor = this.getWlScoreRowColor(rowData);
        if (wlColor) {
          style['background-color'] = wlColor;
          style['font-weight'] = '700';
          style['color'] = '#0b0f14';
        }
      }
    } catch (e) {
      // fall back to base style
    }
    return style;
  }

  shouldWrap(row: any, column: any): boolean {
    const resolved = this.getResolvedCellConfig(row, column);
    return resolved?.wrapText === true
      || resolved?.textWrap === true
      || resolved?.wrap === true
      || column?.wrapText === true
      || column?.textWrap === true
      || column?.wrap === true;
  }

  private logWrapDebug(stage: 'model' | 'dom'): void {
    try {
      const oemColumnIndex = (this.visibleColumns || []).findIndex((column: any) => String(column?.key || '') === 'oem_positioning');
      if (oemColumnIndex < 0) return;

      const configColumn = Array.isArray((this.data as any)?.columns)
        ? (this.data as any).columns.find((column: any) => String(column?.key || '') === 'oem_positioning')
        : null;
      const renderColumn = this.visibleColumns[oemColumnIndex] as any;
      const firstRow = Array.isArray(this.flatRows) && this.flatRows.length > 0 ? this.flatRows[0] : null;
      const firstValue = firstRow ? this.getDisplayValue(firstRow.source, renderColumn) : '';
      const classFromModel = firstRow ? this.getCellClass(firstRow, 0, renderColumn, oemColumnIndex) : '';

      const payload: any = {
        sequence: ++this.wrapDebugSequence,
        stage,
        component: 'custom/config-driven-super-table',
        widgetId: (this.data as any)?.widgetId,
        columnIndex: oemColumnIndex,
        configWrapText: configColumn?.wrapText,
        configTextWrap: configColumn?.textWrap,
        configWrap: configColumn?.wrap,
        renderWrapText: renderColumn?.wrapText,
        renderTextWrap: renderColumn?.textWrap,
        renderWrap: renderColumn?.wrap,
        classFromModel,
        firstValue,
      };

      if (stage === 'dom') {
        const firstBodyRow = this.tableRef?.nativeElement?.querySelector('tbody tr:not(.score-row)') as HTMLTableRowElement | null;
        const cell = firstBodyRow?.children?.[oemColumnIndex] as HTMLElement | undefined;
        const valueElement = cell?.querySelector('.cell-value') as HTMLElement | null;
        const valueStyle = valueElement ? window.getComputedStyle(valueElement) : null;
        payload.dom = {
          tdClass: cell?.className || '',
          tdWidth: cell?.getBoundingClientRect().width || 0,
          hasWrapClass: cell?.classList.contains('wrap-text') || false,
          valueText: valueElement?.textContent?.trim() || '',
          valueClientWidth: valueElement?.clientWidth || 0,
          valueScrollWidth: valueElement?.scrollWidth || 0,
          overflowsX: valueElement ? valueElement.scrollWidth > valueElement.clientWidth + 1 : false,
          valueClientHeight: valueElement?.clientHeight || 0,
          valueScrollHeight: valueElement?.scrollHeight || 0,
          overflowsY: valueElement ? valueElement.scrollHeight > valueElement.clientHeight + 1 : false,
          whiteSpace: valueStyle?.whiteSpace || '',
          overflowWrap: valueStyle?.overflowWrap || '',
          wordBreak: valueStyle?.wordBreak || '',
          lineBreak: valueStyle?.lineBreak || '',
          overflow: valueStyle?.overflow || '',
          textOverflow: valueStyle?.textOverflow || '',
          display: valueStyle?.display || '',
        };
      }

      console.info(`[${WRAP_DEBUG_KEY}]`, payload);
    } catch (error) {
      console.warn(`[${WRAP_DEBUG_KEY}] debug logger failed`, error);
    }
  }

  private getWlScoreRowColor(rowData: any): string | null {
    if (!rowData || typeof rowData !== 'object') return null;

    const wlValue = Number(rowData.wlScore);
    if (!Number.isFinite(wlValue)) return null;

    const wlColumn = (this.visibleColumns || []).find((col: any) => String(col?.key || '') === 'wlScore');
    if (!wlColumn) return null;

    // Use the same resolved config path as WL score cell styling for exact color parity.
    const resolvedWl = this.getResolvedCellConfig(rowData, wlColumn);
    return this.resolveHeatmapColor(wlValue, resolvedWl?.heatmapConfig);
  }

  private resolveHeatmapColor(value: number, heatmapConfig: any): string {
    const min = Number(heatmapConfig?.min);
    const max = Number(heatmapConfig?.max);
    const colors = Array.isArray(heatmapConfig?.colors) && heatmapConfig.colors.length > 0
      ? heatmapConfig.colors
      : ['#ef4444', '#f59e0b', '#facc15', '#a3d977', '#65c07a'];

    const safeMin = Number.isFinite(min) ? min : 0;
    const safeMax = Number.isFinite(max) ? max : 10;
    const range = safeMax - safeMin;
    if (range <= 0) return colors[colors.length - 1];

    const clamped = Math.min(Math.max(value, safeMin), safeMax);
    const bucketSize = range / colors.length;
    if (bucketSize <= 0) return colors[colors.length - 1];

    const idx = Math.min(colors.length - 1, Math.floor((clamped - safeMin) / bucketSize));
    return String(colors[idx] || colors[colors.length - 1]);
  }

  override getRowHeightForIndex(rowIndex: any): any {
    // TODO: Add your custom logic here
    return super.getRowHeightForIndex(rowIndex);
  }

  override getFrozenBodyRowsHeight(rowIndex: any): any {
    // TODO: Add your custom logic here
    return super.getFrozenBodyRowsHeight(rowIndex);
  }

  override getRowClass(row: any, rowIndex: any): any {
    // TODO: Add your custom logic here
    return super.getRowClass(row, rowIndex);
  }

  override getCellClass(row: any, rowIndex: any, column: any, colIndex: any): any {
    const baseClass = String(super.getCellClass(row, rowIndex, column, colIndex) || '');
    const rowData = row?.source || row;
    const classNames = [
      ...baseClass.split(/\s+/),
      this.shouldWrap(rowData, column) ? 'wrap-text' : '',
    ].filter(Boolean);
    return Array.from(new Set(classNames)).join(' ');
  }

  override getIndentPadding(depth: any): any {
    // TODO: Add your custom logic here
    return super.getIndentPadding(depth);
  }

  override resolveCellType(row: any, column: any): any {
    const resolved = this.getResolvedCellConfig(row, column);
    if (resolved?.type === 'serial') return 'serial';

    const disableAutoChartFromText = (this.data as any)?.disableAutoChartFromText === true
      || resolved?.forceText === true;
    if (disableAutoChartFromText) {
      return resolved?.type || 'text';
    }

    return super.resolveCellType(row, column);
  }

  override isCellEditable(row: any, column: any): any {
    const type = this.resolveCellType(row, column);
    if (['action', 'chart', 'link', 'image', 'serial'].includes(type)) return false;

    const resolved = this.getResolvedCellConfig(row, column);
    if (resolved?.disabled) return false;

    // Allow explicit per-column editable=true even if table-level editable is false.
    if (resolved?.editable === true) {
      const rowCfg = this.getRowConfig(row);
      if (rowCfg?.disabled || rowCfg?.editable === false) return false;

      const rowEditableKey = (this.data as any)?.rowEditableKey;
      if (!rowEditableKey) return true;

      const value = this.getValueByPath(row, rowEditableKey);
      const rowEditableWhen = (this.data as any)?.rowEditableWhen;
      if (Array.isArray(rowEditableWhen) && rowEditableWhen.length > 0) {
        return rowEditableWhen.includes(value);
      }

      return Boolean(value);
    }

    return super.isCellEditable(row, column);
  }

  override isRowEditable(row: any): any {
    // TODO: Add your custom logic here
    return super.isRowEditable(row);
  }

  override getResolvedCellConfig(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.getResolvedCellConfig(row, column);
  }

  override getCellValue(row: any, column: any): any {
    const raw = super.getCellValue(row, column);
    try {
      const resolved = (this as any).getResolvedCellConfig?.(row, column) || column;
      if (resolved && resolved.type === 'currency') {
        const num = Number(raw);
        if (Number.isFinite(num)) return this.formatMillions(num);
      }

      if (this.shouldShowPercentage(row, column, resolved)) {
        return this.formatWithPercentage(raw);
      }
    } catch (e) {
      // ignore and fall back
    }
    return raw;
  }

  override getDisplayValue(row: any, column: any): any {
    const rawDisplay = super.getDisplayValue(row, column);
    let resolved: any;
    let rawValue: any;
    try {
      resolved = (this as any).getResolvedCellConfig?.(row, column) || column;
      rawValue = super.getCellValue(row, column);
      if (resolved && resolved.type === 'currency') {
        const num = Number(rawValue);
        if (Number.isFinite(num)) return this.formatMillions(num);
        // if underlying value is a string already formatted, try parsing it
        const parsed = this.parseMillionsInput(String(rawDisplay));
        if (parsed !== null) return this.formatMillions(parsed);
      }

      // Reusable key: set "showPercentage": true on any column to append % in display.
      const showPercentage = this.shouldShowPercentage(row, column, resolved);
      if (showPercentage) {
        return this.formatWithPercentage(rawDisplay);
      }

      const configuredColumn = Array.isArray((this.data as any)?.columns)
        ? (this.data as any).columns.find((col: any) => String(col?.key || '') === String(column?.key || ''))
        : null;
      const configuredPlaces = (resolved as any)?.decimalPlaces
        ?? (configuredColumn as any)?.decimalPlaces
        ?? (this.data as any)?.decimalPlaces;
      const parsedPlaces = Number(configuredPlaces);
      const hasDecimalConfig = Number.isFinite(parsedPlaces) && parsedPlaces >= 0;
      if (
        hasDecimalConfig
        && rawValue !== undefined
        && rawValue !== null
        && rawValue !== ''
        && this.isNumeric(rawValue)
        && resolved?.type !== 'checkbox'
        && resolved?.type !== 'select'
        && resolved?.type !== 'image'
        && resolved?.type !== 'chart'
      ) {
        return Number(rawValue).toFixed(Math.floor(parsedPlaces));
      }
    } catch (e) {
      // fall back to default
    }

    // Round decimal values to 2 places by default for numeric values
    if (this.isNumeric(rawValue) && resolved?.type !== 'currency' && resolved?.type !== 'checkbox' && resolved?.type !== 'select') {
      const num = Number(rawValue);
      if (Number.isFinite(num) && num % 1 !== 0) { // has decimals
        return num.toFixed(2);
      }
    }

    return rawDisplay;
  }

  override resolveImageSource(value: any): any {
    // TODO: Add your custom logic here
    return super.resolveImageSource(value);
  }

  override resolveImageValue(value: any): any {
    // TODO: Add your custom logic here
    return super.resolveImageValue(value);
  }

  override getInputType(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.getInputType(row, column);
  }

  override getPlaceholder(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.getPlaceholder(row, column);
  }

  override resolveOptions(row: any, column: any): any {
    const resolved = (this as any).getResolvedCellConfig?.(row, column) || column;

    // Preserve row-level dynamic options behavior.
    const rowOptions = resolved?.optionsPath ? this.getValueByPath(row, resolved.optionsPath) : undefined;
    if (Array.isArray(rowOptions)) {
      return this.normalizeOptions(rowOptions);
    }

    const staticOptions = Array.isArray(resolved?.options)
      ? resolved.options
      : Array.isArray(column?.options)
        ? column.options
        : null;

    if (!Array.isArray(staticOptions)) {
      return super.resolveOptions(row, column);
    }

    const cacheKey = String(resolved?.key || column?.key || '__default_select__');
    const signature = this.getOptionsSignature(staticOptions);
    const cached = this.selectOptionsCache[cacheKey];
    if (cached && cached.signature === signature) {
      return cached.options;
    }

    const normalized = this.normalizeOptions(staticOptions);
    this.selectOptionsCache[cacheKey] = { signature, options: normalized };
    return normalized;
  }

  private getOptionsSignature(options: any[]): string {
    return options
      .map((item: any) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          return `${String(item.label ?? item.name ?? '')}:${String(item.value ?? item.id ?? item.key ?? '')}`;
        }
        return String(item);
      })
      .join('|');
  }

  override normalizeOptions(options: any): any {
    // TODO: Add your custom logic here
    return super.normalizeOptions(options);
  }

  override onTextInput(row: any, column: any, event: any): any {
    try {
      const resolved = (this as any).getResolvedCellConfig?.(row, column) || column;
      const rawValue = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
      if (resolved && resolved.type === 'currency') {
        const parsed = this.parseMillionsInput(rawValue);
        if (parsed !== null) {
          return this.applyCellValue(row, column, parsed);
        }
        return; // ignore invalid input
      }
    } catch (e) {
      // fall back to default handler
    }

    return super.onTextInput(row, column, event);
  }

  override onSelectChange(row: any, column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onSelectChange(row, column, event);
  }

  override onCheckboxChange(row: any, column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onCheckboxChange(row, column, event);
  }

  override applyCellValue(row: any, column: any, value: any): any {
    const result = super.applyCellValue(row, column, value);
    this.trackEditedCell(row, column, value);
    return result;
  }

  override getTooltipText(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.getTooltipText(row, column);
  }

  override hasPopup(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.hasPopup(row, column);
  }

  override resolvePopupConfig(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.resolvePopupConfig(row, column);
  }

  override togglePopup(row: any, column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.togglePopup(row, column, event);
  }

  override loadPopupFromApi(row: any, column: any, rect: any, popup: any, apiUrl: any, requestId: any): any {
    // TODO: Add your custom logic here
    return super.loadPopupFromApi(row, column, rect, popup, apiUrl, requestId);
  }

  override isPopupRequestActive(rowId: any, columnKey: any, requestId: any): any {
    // TODO: Add your custom logic here
    return super.isPopupRequestActive(rowId, columnKey, requestId);
  }

  override buildPopupRequestConfig(row: any, column: any, popup: any): any {
    // TODO: Add your custom logic here
    return super.buildPopupRequestConfig(row, column, popup);
  }

  override buildPopupFilters(rowData: any, popup: any): any {
    // TODO: Add your custom logic here
    return super.buildPopupFilters(rowData, popup);
  }

  override resolvePopupParamMap(map: any, rowData: any): any {
    // TODO: Add your custom logic here
    return super.resolvePopupParamMap(map, rowData);
  }

  override resolvePopupBindingValue(binding: any, rowData: any): any {
    // TODO: Add your custom logic here
    return super.resolvePopupBindingValue(binding, rowData);
  }

  override buildExternalContext(action?: any): any {
    // TODO: Add your custom logic here
    return super.buildExternalContext(action);
  }

  override resolveFilterTemplate(value: any, context: any): any {
    // TODO: Add your custom logic here
    return super.resolveFilterTemplate(value, context);
  }

  override buildExternalFilters(queryParamsMap: any, requestParamsMap: any, baseFilters: any, useQueryParams: any, action?: any): any {
    // TODO: Add your custom logic here
    return super.buildExternalFilters(queryParamsMap, requestParamsMap, baseFilters, useQueryParams, action);
  }

  override resolveExternalParamMap(map: any, context: any): any {
    // TODO: Add your custom logic here
    return super.resolveExternalParamMap(map, context);
  }

  override resolveExternalBindingValue(binding: any, context: any): any {
    // TODO: Add your custom logic here
    return super.resolveExternalBindingValue(binding, context);
  }

  override resolveContextPath(path: any, context: any): any {
    // TODO: Add your custom logic here
    return super.resolveContextPath(path, context);
  }

  override resolveRowTemplate(value: any, rowData: any): any {
    // TODO: Add your custom logic here
    return super.resolveRowTemplate(value, rowData);
  }

  override resolvePopupSource(popup: any, rowSource: any, apiResponse?: any): any {
    // TODO: Add your custom logic here
    return super.resolvePopupSource(popup, rowSource, apiResponse);
  }

  override resolvePopupApiUrl(popup: any, rowSource: any): any {
    // TODO: Add your custom logic here
    return super.resolvePopupApiUrl(popup, rowSource);
  }

  override resolvePopupType(popup: any, source: any): any {
    // TODO: Add your custom logic here
    return super.resolvePopupType(popup, source);
  }

  override buildPopupState(row: any, column: any, rect: any, popupOverride?: any, apiResponse?: any, loading: any = false, error: any = '', requestId: any = 0): any {
    // TODO: Add your custom logic here
    return super.buildPopupState(row, column, rect, popupOverride, apiResponse, loading, error, requestId);
  }

  override closePopup(): any {
    // TODO: Add your custom logic here
    return super.closePopup();
  }

  override onPopupPanelClick(event: any): any {
    // TODO: Add your custom logic here
    return super.onPopupPanelClick(event);
  }

  override onDocumentClick(event: any): any {
    // TODO: Add your custom logic here
    return super.onDocumentClick(event);
  }

  override onEscapePress(): any {
    // TODO: Add your custom logic here
    return super.onEscapePress();
  }

  override onWindowResize(): any {
    // TODO: Add your custom logic here
    return super.onWindowResize();
  }

  override getPopupStyle(): any {
    // TODO: Add your custom logic here
    return super.getPopupStyle();
  }

  override onControlValueChange(control: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onControlValueChange(control, event);
  }

  override onControlClick(control: any, event?: any): any {
    // TODO: Add your custom logic here
    return super.onControlClick(control, event);
  }

  override handleControlAction(control: any, value: any): any {
    // TODO: Add your custom logic here
    return super.handleControlAction(control, value);
  }

  override onTableAction(action: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onTableAction(action, event);
  }

  override isTableActionBusy(action: any): any {
    // TODO: Add your custom logic here
    return super.isTableActionBusy(action);
  }

  override getTableActionLoadingKey(action: any): any {
    // TODO: Add your custom logic here
    return super.getTableActionLoadingKey(action);
  }

  override emitTableActionEvent(action: any, response?: any, error?: any): any {
    this.applyActionResponseToRows(action, response, error);

    if (!error && action?.refreshTableAfterSuccess === true) {
      const delayMs = Math.max(0, Number(action?.refreshDelayMs ?? 0));
      setTimeout(() => {
        this.loadRows(this.currentFilters);
      }, delayMs);
    }

    return super.emitTableActionEvent(action, response, error);
  }

  override executeTableActionApi(action: any): any {
    // TODO: Add your custom logic here
    return super.executeTableActionApi(action);
  }

  override buildTableActionRequestConfig(action: any): any {
    const base = super.buildTableActionRequestConfig(action);
    const payloadCfg = action?.editedPayload;
    if (!payloadCfg || payloadCfg.enabled !== true) {
      return base;
    }

    const editedRequestData = this.buildEditedRequestData(payloadCfg);

    return {
      ...base,
      skipDefaultPayload: true,
      headers: action?.headers || (this.data as any)?.headers || {},
      method: action?.method || 'post',
      customPayload: {
        requestData: editedRequestData,
        responseList: [],
        entity: action?.entity || 'default_entity',
        workflowId: action?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: action?.functionName || 'DefaultService',
            version: action?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: action?.version || '1.0',
      },
    };
  }

  private trackEditedCell(row: any, column: any, value: any): void {
    const payloadCfg = this.getEditedPayloadConfig();
    if (!payloadCfg?.enabled) return;

    const sourceRow = row?.source && typeof row.source === 'object' ? row.source : row;
    const identityKey = String(payloadCfg.identityKey || 'oem_name');
    const identityValue = this.getValueByPath(sourceRow, identityKey);
    if (identityValue === undefined || identityValue === null || identityValue === '') return;

    const identity = String(identityValue);
    if (!this.editedRowsByIdentity[identity]) {
      this.editedRowsByIdentity[identity] = {};
    }

    this.editedRowsByIdentity[identity][String(column?.key || '')] = value;
    this.editedRowsByIdentity[identity][identityKey] = identityValue;
    this.lastEditedIdentity = identity;
  }

  private getEditedPayloadConfig(): any {
    return (this.data as any)?.editedPayload || null;
  }

  private buildEditedRequestData(payloadCfg: any): any {
    const identityKey = String(payloadCfg?.identityKey || 'oem_name');
    const renameMap = payloadCfg?.rename && typeof payloadCfg.rename === 'object' ? payloadCfg.rename : {};
    const forceInclude = Array.isArray(payloadCfg?.forceInclude)
      ? payloadCfg.forceInclude.map((k: any) => String(k))
      : [identityKey];
    const includeEditedOnly = payloadCfg?.includeEditedOnly !== false;
    const mode = String(payloadCfg?.mode || 'single').toLowerCase();

    const identities = Object.keys(this.editedRowsByIdentity);
    if (identities.length === 0) {
      return {};
    }

    const sourceRows = Array.isArray(this.rawRows) ? this.rawRows : [];
    const sourceByIdentity: Record<string, any> = {};
    sourceRows.forEach((r: any) => {
      const idVal = this.getValueByPath(r, identityKey);
      if (idVal !== undefined && idVal !== null && String(idVal) !== '') {
        sourceByIdentity[String(idVal)] = r;
      }
    });

    const buildOne = (identity: string): any => {
      const edited = this.editedRowsByIdentity[identity] || {};
      const source = sourceByIdentity[identity] || {};
      const payload: any = {};

      Object.keys(edited).forEach((key: string) => {
        if (!key) return;
        const outKey = String(renameMap[key] || key);
        payload[outKey] = edited[key];
      });

      forceInclude.forEach((key: string) => {
        const outKey = String(renameMap[key] || key);
        if (payload[outKey] !== undefined) return;
        const value = edited[key] !== undefined ? edited[key] : this.getValueByPath(source, key);
        if (value !== undefined) payload[outKey] = value;
      });

      if (!includeEditedOnly && Array.isArray(payloadCfg?.include)) {
        payloadCfg.include.forEach((key: any) => {
          const srcKey = String(key);
          const outKey = String(renameMap[srcKey] || srcKey);
          if (payload[outKey] !== undefined) return;
          const value = this.getValueByPath(source, srcKey);
          if (value !== undefined) payload[outKey] = value;
        });
      }

      return payload;
    };

    if (mode === 'rows' || mode === 'array') {
      return identities.map((identity) => buildOne(identity));
    }

    if (mode === 'combined' || mode === 'merge' || mode === 'merged' || mode === 'columns') {
      const separator = String(payloadCfg?.separator || ',');
      const aggregateAsArray = payloadCfg?.aggregateAsArray === true;
      const combined: Record<string, any[]> = {};

      identities.forEach((identity) => {
        const rowPayload = buildOne(identity);
        if (!rowPayload || typeof rowPayload !== 'object') return;

        Object.keys(rowPayload).forEach((key: string) => {
          const value = rowPayload[key];
          if (value === undefined || value === null || value === '') return;

          if (!combined[key]) {
            combined[key] = [];
          }
          combined[key].push(value);
        });
      });

      if (aggregateAsArray) {
        return combined;
      }

      const flattened: Record<string, string> = {};
      Object.keys(combined).forEach((key: string) => {
        flattened[key] = combined[key].map((v: any) => String(v)).join(separator);
      });
      return flattened;
    }

    const identity = this.lastEditedIdentity && this.editedRowsByIdentity[this.lastEditedIdentity]
      ? this.lastEditedIdentity
      : identities[identities.length - 1];
    return buildOne(identity);
  }

  override resolveTableActionResponse(action: any, response: any): any {
    // TODO: Add your custom logic here
    return super.resolveTableActionResponse(action, response);
  }

  private applyActionResponseToRows(action: any, response?: any, error?: any): void {
    if (error) return;
    const shouldApply = action?.applyResponseToTable === true;
    if (!shouldApply) return;

    const wrappedRows = this.getValueByPath(response, 'responseList.0.data');
    const responseRows = Array.isArray(response)
      ? response
      : Array.isArray(wrappedRows)
        ? wrappedRows
        : response && typeof response === 'object'
          ? [response]
          : [];
    if (responseRows.length === 0) return;

    const payloadCfg = action?.editedPayload || this.getEditedPayloadConfig() || {};
    const identityKey = String(payloadCfg?.identityKey || 'oem_name');

    const byIdentity: Record<string, any> = {};
    responseRows.forEach((r: any) => {
      const id = this.getValueByPath(r, identityKey);
      if (id !== undefined && id !== null && String(id) !== '') {
        byIdentity[String(id)] = r;
      }
    });

    const hasIdentityRows = Object.keys(byIdentity).length > 0;
    const fallbackIdentity = this.lastEditedIdentity;

    const rawRows = Array.isArray(this.rawRows) ? this.rawRows : [];
    this.rawRows = rawRows.map((row: any) => {
      const id = this.getValueByPath(row, identityKey);
      let incoming: any = null;

      if (id !== undefined && id !== null && String(id) !== '' && hasIdentityRows) {
        incoming = byIdentity[String(id)] || null;
      }

      // Fallback: when API returns only recalculated fields (e.g., weighted_score)
      // without identity key, apply response to the last edited row for single mode.
      if (!incoming && !hasIdentityRows && fallbackIdentity && String(id) === String(fallbackIdentity)) {
        incoming = responseRows[0];
      }

      if (!incoming) return row;
      return {
        ...row,
        ...incoming,
      };
    });

    this.rebuildTableModel();
    this.cdr.markForCheck();
  }

  override getCellActions(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.getCellActions(row, column);
  }

  override isActionVisible(action: any, row: any): any {
    // TODO: Add your custom logic here
    return super.isActionVisible(action, row);
  }

  override isActionDisabled(action: any, row: any): any {
    // TODO: Add your custom logic here
    return super.isActionDisabled(action, row);
  }

  override onCellAction(action: any, row: any, column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onCellAction(action, row, column, event);
  }

  override onCellNavigate(row: any, column: any, event: any): any {
    // TODO: Add your custom logic here
    return super.onCellNavigate(row, column, event);
  }

  override navigate(navigation: any, row: any): any {
    // TODO: Add your custom logic here
    return super.navigate(navigation, row);
  }

  override hasChartPayload(value: any): any {
    // TODO: Add your custom logic here
    return super.hasChartPayload(value);
  }

  override getConfiguredRowIdKey(): any {
    // TODO: Add your custom logic here
    return super.getConfiguredRowIdKey();
  }

  override getConfiguredRowLabelKey(): any {
    // TODO: Add your custom logic here
    return super.getConfiguredRowLabelKey();
  }

  override getRowIdentifier(row: any): any {
    // TODO: Add your custom logic here
    return super.getRowIdentifier(row);
  }

  override resolveChartConfig(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.resolveChartConfig(row, column);
  }

  override resolveTypeConfig(row: any, chartConfig: any, resolvedType: any): any {
    // TODO: Add your custom logic here
    return super.resolveTypeConfig(row, chartConfig, resolvedType);
  }

  override resolveChartType(row: any, chartConfig: any, rawValue: any): any {
    // TODO: Add your custom logic here
    return super.resolveChartType(row, chartConfig, rawValue);
  }

  override inferSingleTypeFromConfigMap(chartConfig: any): any {
    // TODO: Add your custom logic here
    return super.inferSingleTypeFromConfigMap(chartConfig);
  }

  override normalizeDynamicType(type: any): any {
    // TODO: Add your custom logic here
    return super.normalizeDynamicType(type);
  }

  override buildDynamicWidgetConfig(base: any, typeConfig: any, payload: any, rawValue: any): any {
    // TODO: Add your custom logic here
    return super.buildDynamicWidgetConfig(base, typeConfig, payload, rawValue);
  }

  override resolveChartPayload(row: any, chartConfig: any, rawValue: any): any {
    // TODO: Add your custom logic here
    return super.resolveChartPayload(row, chartConfig, rawValue);
  }

  override stripChartMetaConfig(chartConfig: any): any {
    // TODO: Add your custom logic here
    return super.stripChartMetaConfig(chartConfig);
  }

  override isInlineMiniChart(chart: any): any {
    // TODO: Add your custom logic here
    return super.isInlineMiniChart(chart);
  }

  override isInlineMiniChartType(type: any): any {
    // TODO: Add your custom logic here
    return super.isInlineMiniChartType(type);
  }

  override getCellDynamicChartTarget(row: any, column: any, chart: any): any {
    // TODO: Add your custom logic here
    return super.getCellDynamicChartTarget(row, column, chart);
  }

  override getPopupDynamicChartTarget(chart: any): any {
    // TODO: Add your custom logic here
    return super.getPopupDynamicChartTarget(chart);
  }

  override queueStickyOffsetCalibration(): any {
    // TODO: Add your custom logic here
    return super.queueStickyOffsetCalibration();
  }

  override calibrateStickyOffsetsFromDom(): any {
    // TODO: Add your custom logic here
    return super.calibrateStickyOffsetsFromDom();
  }

  override resolveReferenceCellsForStickyCalibration(table: any): any {
    // TODO: Add your custom logic here
    return super.resolveReferenceCellsForStickyCalibration(table);
  }

  override getMeasuredElementWidth(cell: any, fallback: any): any {
    // TODO: Add your custom logic here
    return super.getMeasuredElementWidth(cell, fallback);
  }

  override snapStickyCoordinate(value: any): any {
    // TODO: Add your custom logic here
    return super.snapStickyCoordinate(value);
  }

  override buildDynamicChartTarget(chart: any, row: any, column: any, isPopup: any): any {
    // TODO: Add your custom logic here
    return super.buildDynamicChartTarget(chart, row, column, isPopup);
  }

  override normalizeChartConfig(raw: any): any {
    // TODO: Add your custom logic here
    return super.normalizeChartConfig(raw);
  }

  override getChartType(chart: any): any {
    // TODO: Add your custom logic here
    return super.getChartType(chart);
  }

  override getProgressColor(chart: any): any {
    // TODO: Add your custom logic here
    return super.getProgressColor(chart);
  }

  override getChartHeight(chart: any, fallback: any): any {
    // TODO: Add your custom logic here
    return super.getChartHeight(chart, fallback);
  }

  override getProgressPercent(chart: any): any {
    // TODO: Add your custom logic here
    return super.getProgressPercent(chart);
  }

  override getRowConfig(row: any): any {
    // TODO: Add your custom logic here
    return super.getRowConfig(row);
  }

  override getCellConfig(row: any, columnKey: any): any {
    // TODO: Add your custom logic here
    return super.getCellConfig(row, columnKey);
  }

  override getRowConfigKey(): any {
    // TODO: Add your custom logic here
    return super.getRowConfigKey();
  }

  override getCellConfigKey(): any {
    // TODO: Add your custom logic here
    return super.getCellConfigKey();
  }

  override getReservedKeys(): any {
    // TODO: Add your custom logic here
    return super.getReservedKeys();
  }

  override handleCellClick(row: any, column: any): any {
    // TODO: Add your custom logic here
    return super.handleCellClick(row, column);
  }

  override emitCustomEvent(eventName: any, payload: any): any {
    // TODO: Add your custom logic here
    return super.emitCustomEvent(eventName, payload);
  }

  override usesRemoteData(): any {
    // TODO: Add your custom logic here
    return super.usesRemoteData();
  }

  override resolveMethod(url: any): any {
    // TODO: Add your custom logic here
    return super.resolveMethod(url);
  }

  override buildTableColumnsFromRows(rows: any): any {
    // TODO: Add your custom logic here
    return super.buildTableColumnsFromRows(rows);
  }

  override getPopupChart(): any {
    // TODO: Add your custom logic here
    return super.getPopupChart();
  }

  override normalizeCssSize(value: any): any {
    // TODO: Add your custom logic here
    return super.normalizeCssSize(value);
  }

  override toPixels(value: any, fallback: any): any {
    // TODO: Add your custom logic here
    return super.toPixels(value, fallback);
  }

  override isNumeric(value: any): any {
    // TODO: Add your custom logic here
    return super.isNumeric(value);
  }

  override isDateLike(value: any): any {
    // TODO: Add your custom logic here
    return super.isDateLike(value);
  }

  override areValuesEqual(a: any, b: any): any {
    // TODO: Add your custom logic here
    return super.areValuesEqual(a, b);
  }

  override getValueByPath(source: any, path: any): any {
    // TODO: Add your custom logic here
    return super.getValueByPath(source, path);
  }

  override humanizeKey(value: any): any {
    // TODO: Add your custom logic here
    return super.humanizeKey(value);
  }

  override cloneRows(rows: any): any {
    // TODO: Add your custom logic here
    return super.cloneRows(rows);
  }

  override cloneObject(value: any): any {
    // TODO: Add your custom logic here
    return super.cloneObject(value);
  }

  override getColumnLeftOffset(index: any): any {
    // TODO: Add your custom logic here
    return super.getColumnLeftOffset(index);
  }

  override getFrozenRowTopOffset(rowIndex: any): any {
    // TODO: Add your custom logic here
    return super.getFrozenRowTopOffset(rowIndex);
  }
}
